import React, { FC, useRef, useState } from 'react';
import cx from 'classnames';
import { compact } from 'lodash';
import { stringify } from 'qs';
import { Trans } from '@lingui/macro';
import { PropsWithClassName } from '@/types/common';
import Steps from '@/components/Common/Steps';
import Button, { ButtonVariant } from '@/components/Common/Button';
import ArrowIcon from '@/assets/icons/arrow.svg';
import {
  BundleOption,
  VariationView,
  ConfiguredBundleOption,
  ConfiguredProductCharacteristic,
} from '@/types/index';
import {
  configureDefaultProductBundleOption,
  serializeConfiguredProductBundle,
} from '@/utils/configurator';
import DownloadButton from '@/components/Common/DownloadButton';
import Asset, { AssetType } from '@/components/Common/Asset';
import BundleOptionProductSelector from './BundleOptionProductSelector';
import ProductCharacteristicSelector from './ProductCharacteristicSelector';
import ConfiguredProductPreview from './ConfiguredProductPreview';
import * as styles from './ProductConfigurator.module.scss';

const isConfigurableCharacteristic = (c: BundleOption['characteristics'][number]) =>
  c.title && c.view;

type Step = {
  optionIndex: number;
  characteristicIndex: number | null;
  view: VariationView;
  title: string;
  isFirst?: boolean;
};

const makeStep = (
  title: string,
  optionIndex: number,
  characteristicIndex: number | null,
  view: VariationView
): Step => ({
  optionIndex,
  characteristicIndex,
  view,
  title,
});

const getOptionAsSteps = (option: BundleOption, optionIndex = 0): Step[] => {
  const internalSteps = option.characteristics
    ?.map((characteristic, characteristicIndex) =>
      isConfigurableCharacteristic(characteristic)
        ? makeStep(
            characteristic.title,
            optionIndex,
            characteristicIndex,
            characteristic.view
          )
        : null
    )
    .filter(Boolean);
  return internalSteps?.length > 0
    ? compact(internalSteps)
    : [
        /*
         * Since product selection goes in one of the steps, we need to artificially add
         * a step if there aren't any configurable characteristics.
         */
        makeStep(option.title, optionIndex, null, VariationView.Image),
      ];
};

/**
 * We make a flat list of steps from the options and characteristics.
 */
const getOptionsAsSteps = (options: BundleOption[]): Step[] => {
  const steps = options.map(getOptionAsSteps);
  return steps.map(([first, ...rest]) => [{ ...first, isFirst: true }, ...rest]).flat();
};

const isIncompleteStep = (
  options: BundleOption[],
  value: ConfiguredBundleOption[],
  step: Step
) => {
  return options[step.optionIndex].isRequired && !value[step.optionIndex]?.sku;
};

type Props = PropsWithClassName<{
  sku: string;
  options: BundleOption[];
  defaultValue: ConfiguredBundleOption[];
  onDone: (options: ConfiguredBundleOption[]) => void;
  shouldUseFallbackImage?: boolean;
  fallbackImage?: AssetType;
}>;

const BundleConfigurator: FC<Props> = ({
  sku,
  options,
  defaultValue,
  shouldUseFallbackImage,
  fallbackImage,
  onDone,
}) => {
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const steps = getOptionsAsSteps(options);
  const currentStep = steps[currentStepIndex];
  const configuration = useRef<HTMLDivElement>();

  const { optionIndex = 0, characteristicIndex = 0, view } = currentStep || {};

  const handleStepChange = (index: number) => {
    setCurrentStepIndex(index);
  };

  const handleSelectOptionProduct = (sku: string | null) => {
    setSelectedValue((prev) => {
      const updated = [...prev];
      const product = options[optionIndex]?.products.find((p) => p.sku === sku);
      updated[optionIndex] = product
        ? configureDefaultProductBundleOption(product)
        : null;
      return updated;
    });
  };

  const handleSelectOptionCharacteristic = (value: ConfiguredProductCharacteristic) => {
    setSelectedValue((prev) => {
      const updated = [...prev];
      if (characteristicIndex !== null && updated[optionIndex]) {
        updated[optionIndex].characteristics[characteristicIndex] = value;
      }
      return updated;
    });
  };

  const handleAddToEstimate = () => {
    onDone(selectedValue);
  };

  const activeProduct = options[optionIndex].products.find(
    (p) => p.sku === selectedValue[optionIndex]?.sku
  );

  const isStepComplete = !isIncompleteStep(options, selectedValue, currentStep);
  const isDone = isStepComplete && currentStepIndex === steps.length - 1;

  const isCompactProductSelector = [
    VariationView.SeatBackCushion,
    VariationView.Material,
  ].includes(view);

  const serialized = serializeConfiguredProductBundle({
    sku,
    options: selectedValue,
    quantity: 1,
  });

  const downloadUrl = `${process.env.GATSBY_API_URL}/preview?${stringify({
    product: sku,
    options: serialized[1],
    download: true,
  })}`;

  return (
    <div className={styles.main}>
      <Steps
        activeIndex={currentStepIndex}
        steps={steps.map((step, index) => ({
          title: step.title,
          isDisabled:
            steps[index - 1] &&
            isIncompleteStep(options, selectedValue, steps[index - 1]),
        }))}
        onChange={handleStepChange}
      />

      <div
        ref={(node) => (configuration.current = node!)}
        className={styles.configuration}
      >
        <div className={styles.options}>
          {currentStep.isFirst && currentStep.view !== VariationView.None && (
            <div
              className={cx(styles.products, {
                [styles.productsCompact]: isCompactProductSelector,
              })}
            >
              <BundleOptionProductSelector
                option={options[optionIndex]}
                value={selectedValue[optionIndex]?.sku || null}
                onChange={handleSelectOptionProduct}
                characteristicIndex={characteristicIndex}
              />
            </div>
          )}

          {activeProduct && characteristicIndex !== null ? (
            <div className={styles.characteristics}>
              <ProductCharacteristicSelector
                className={styles.samples}
                product={activeProduct}
                view={view}
                characteristicIndex={characteristicIndex}
                onChange={handleSelectOptionCharacteristic}
                value={
                  selectedValue[optionIndex]?.characteristics[characteristicIndex] ?? null
                }
              />
            </div>
          ) : null}
        </div>

        {shouldUseFallbackImage ? (
          <div className={cx(styles.preview, styles.fallbackPreview)}>
            <Asset
              asset={fallbackImage}
              className={styles.fallbackImage}
              shouldUseCustomImage
            />
          </div>
        ) : (
          <ConfiguredProductPreview
            className={styles.preview}
            product={{
              sku,
              quantity: 1,
              options: selectedValue,
            }}
          />
        )}
      </div>

      <div className={styles.navigation}>
        {
          <Button
            variant={ButtonVariant.Bare}
            onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
            className={styles.previous}
            disabled={currentStepIndex === 0}
          >
            <ArrowIcon />
            <Trans>Back</Trans>
          </Button>
        }

        {currentStepIndex < steps.length - 1 ? (
          <Button
            variant={ButtonVariant.Bare}
            disabled={!isStepComplete}
            onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
            className={styles.next}
          >
            <Trans>Next step</Trans>
            <ArrowIcon />
          </Button>
        ) : isDone ? (
          <div className={styles.actions}>
            <DownloadButton
              className={styles.downloadImageButton}
              file={{
                filename: '',
                title: sku,
                url: downloadUrl,
              }}
              title={<Trans>Download image</Trans>}
            />
            <Button
              disabled={!isStepComplete}
              variant={ButtonVariant.Primary}
              onClick={handleAddToEstimate}
              className={styles.previous}
            >
              <Trans>Add to estimate</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BundleConfigurator;
